/**
 * @file `PartAssessmentPage` — `/courses/:courseId/:partId/assessment`.
 * @module @labcraft/lab/ui/pages/part_assessment
 *
 * SPA-side consumer of the Part-boundary CAT mastery assessment endpoint
 * (`GET /api/courses/:courseId/:partId/assessment`, umbrella #1357). The
 * page reads its URL, fetches the Part's consolidated assessment bank +
 * course graph + chapters + the learner's Part-level pass state, then drives
 * the pure {@link PartAssessmentController} CAT engine over it via the shared
 * `<AssessmentPhase>` surface.
 *
 * This is "the flip": mastery is assessed ONCE at the Part boundary against
 * the whole Part's bank, replacing the retired per-chapter quiz. On the CAT's
 * `pass` verdict the page persists the pass (`recordPartAssessment` — the
 * write side of the soft gate, so a later advance into a dependent Part
 * clears) and offers a "Back to course" affordance. On a `fail` it resolves
 * each weak concept to a remediation link back to the owning chapter and
 * hands those to the fail screen.
 *
 * The fetch/loading/error shell mirrors the chapter reader; the stateful CAT
 * runner is a child ({@link AssessmentRunner}) mounted only once the bank has
 * loaded, so the `QuizState` is initialised exactly once with real data (no
 * conditional-hook hazard).
 */

import { useCallback, useEffect, useMemo, useState, type JSX } from 'react'
import { useNavigate, useParams } from 'react-router'
import { useClient } from '@labcraft/core/frontend/react'
import {
  DEFAULT_QUIZ_CONFIG,
  PartAssessmentController,
  QuizEngine,
  type QuizControl,
  type QuizState,
} from '@labcraft/course-kit/runtime'
import {
  QuizSerde,
  type CourseGraph,
  type NodeId,
  type PartAssessment,
} from '@labcraft/course-kit'
import {
  AssessmentPhase,
  type RemediationLink,
} from '../assessment/assessment_phase.tsx'
import type { Lab } from '../../client/index.ts'
import type {
  PartAssessmentChapter,
  PartAssessmentResponse,
} from '../../shared/routes.ts'
import { useRecordPartAssessmentMutation } from '../hooks/use_set_chapter.ts'
import styles from './part_assessment.module.css'

/**
 * An empty course-graph region — the fallback when the manifest carries no
 * graph. With no `requires` edges the draw policy's predecessor set is empty,
 * so {@link QuizEngine.drawConcept} rotates across the Part's concept SET
 * only; interleaving of upstream predecessors never fires.
 */
const EMPTY_GRAPH: CourseGraph = { course: '', nodes: [], edges: [] }

/**
 * Fetcher state for the Part-assessment endpoint — mirrors the chapter
 * reader's discriminated fetch shape.
 */
type FetcherState =
  | { state: 'loading' }
  | { state: 'ok'; response: PartAssessmentResponse }
  | { state: 'error'; error: string }

/**
 * The Part-assessment page. Fetches the endpoint, renders a loading / fatal
 * overlay while pending / on error, and mounts {@link AssessmentRunner} on
 * success. Route chrome (`AuthGuard` + `ReaderErrorBoundary`) is applied by
 * the router entry, so this component owns only the fetch + runner.
 *
 * @returns the Part-assessment page element, or `null` on empty params
 */
export function PartAssessmentPage(): JSX.Element | null {
  const app = useClient<Lab>()
  const navigate = useNavigate()
  const params = useParams()
  const courseId = params.courseId ?? ''
  const partId = params.partId ?? ''

  const [fetched, setFetched] = useState<FetcherState>({ state: 'loading' })
  const [reloadKey, setReloadKey] = useState(0)
  const refetch = useCallback(() => setReloadKey(k => k + 1), [])

  useEffect(() => {
    if (!courseId || !partId) return
    let cancelled = false
    setFetched({ state: 'loading' })
    app.chapter
      .getPartAssessment(courseId, partId)
      .then(response => {
        if (!cancelled) setFetched({ state: 'ok', response })
      })
      .catch((err: unknown) => {
        if (cancelled) return
        setFetched({
          state: 'error',
          error: err instanceof Error ? err.message : String(err),
        })
      })
    return () => {
      cancelled = true
    }
  }, [app, courseId, partId, reloadKey])

  // Bounce empty URLs (the router shouldn't allow this, but defend).
  useEffect(() => {
    if (!courseId || !partId) navigate('/courses', { replace: true })
  }, [courseId, partId, navigate])

  if (!courseId || !partId) return null

  if (fetched.state === 'loading') {
    return (
      <div className={styles.fatal} role="status" aria-live="polite">
        <div className={styles.fatalCard}>
          <span className={styles.fatalEyebrow}>Loading</span>
          <h2 className={styles.fatalTitle}>Preparing your assessment…</h2>
          <p className={styles.fatalBody}>One moment.</p>
        </div>
      </div>
    )
  }

  if (fetched.state === 'error') {
    return (
      <div className={styles.fatal} role="alertdialog" aria-modal>
        <div className={styles.fatalCard}>
          <span className={styles.fatalEyebrow}>Failed to load</span>
          <h2 className={styles.fatalTitle}>Something went wrong.</h2>
          <p className={styles.fatalBody}>{fetched.error}</p>
          <button
            className={styles.fatalBtn}
            type="button"
            onClick={refetch}
            autoFocus>
            Try again
          </button>
        </div>
      </div>
    )
  }

  return (
    <AssessmentRunner
      courseId={courseId}
      partId={partId}
      data={fetched.response}
    />
  )
}

interface AssessmentRunnerProps {
  courseId: string
  partId: string
  data: PartAssessmentResponse
}

/**
 * The stateful CAT runner, mounted once the assessment bank has loaded. Owns
 * the `QuizState` (initialised exactly once from the loaded bank), derives the
 * memoised {@link QuizControl}, resolves remediation links from the settled
 * view's weak concepts, and persists the pass on the `pass` verdict.
 *
 * `PartAssessmentController.view` calls `QuizEngine.selectNext`, which consumes
 * `rng()` when a drawn cell has more than one unused item — so the view is
 * memoised on the `QuizState` identity, not recomputed every render, keeping
 * the current question stable across re-renders.
 *
 * @param props.courseId - the course slug
 * @param props.partId - the Part slug
 * @param props.data - the fetched Part-assessment payload
 * @returns the assessment runner element
 */
function AssessmentRunner(props: AssessmentRunnerProps): JSX.Element {
  const { courseId, partId, data } = props
  const navigate = useNavigate()
  const recordPass = useRecordPartAssessmentMutation()

  // Rehydrate the bank's `RegExp` accept entries (they crossed JSON on the
  // wire as `__lcRegex` tags) and cast the graph region to the branded
  // `CourseGraph`, exactly as the chapter reader does with its spec / graph.
  const assessment = useMemo(
    () => QuizSerde.rehydratePartAssessment(data.assessment as PartAssessment),
    [data.assessment],
  )
  const graph = useMemo(
    () => (data.graph as CourseGraph | undefined) ?? EMPTY_GRAPH,
    [data.graph],
  )

  const [state, setState] = useState<QuizState>(() =>
    PartAssessmentController.initState(assessment, DEFAULT_QUIZ_CONFIG, graph),
  )

  // Memoised on the state identity (see the class doc): re-running `view`
  // (hence `selectNext`) every render would reshuffle the drawn question.
  const view = useMemo(() => PartAssessmentController.view(state), [state])

  const control = useMemo<QuizControl>(
    () => ({
      view,
      submit: submission => {
        const question = view.question
        if (!question) return
        setState(prev =>
          QuizEngine.apply(
            prev,
            question,
            QuizEngine.grade(question, submission),
          ),
        )
      },
      retry: () =>
        setState(
          PartAssessmentController.initState(
            assessment,
            DEFAULT_QUIZ_CONFIG,
            graph,
          ),
        ),
    }),
    [view, assessment, graph],
  )

  // Resolve each weak concept to a remediation link back to its owning
  // chapter. An authored `RemediationTarget` (keyed by concept) wins; else
  // the chapter whose taught `concept` matches supplies the id + objectives.
  // Concepts that resolve to no chapter are dropped rather than dead-linked.
  const remediation = useMemo<RemediationLink[]>(
    () =>
      view.status === 'fail'
        ? resolveRemediation(view.weakConcepts, assessment, data.chapters, {
            courseId,
            partId,
          })
        : [],
    [
      view.status,
      view.weakConcepts,
      assessment,
      data.chapters,
      courseId,
      partId,
    ],
  )

  // #1446 Part B — persist-before-route. The pass must be recorded in
  // `learner_part_assessment` BEFORE the learner is routed to the closing;
  // otherwise the landing (which derives Done from that table) would still
  // read not-done and the loop would persist. So the pass score is kept for
  // a retry, and the footer's forward action gates on the write's success —
  // a failed write surfaces + retries instead of proceeding as passed.
  const [passScore, setPassScore] = useState<number | null>(null)
  const persistPass = useCallback(
    (score: number): void => {
      recordPass.mutate({ courseId, partId, score, band: String(state.band) })
    },
    // `state.band` is read at fire time via the mutate closure; the callback
    // identity only needs to change on course/part change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [recordPass, courseId, partId, state.band],
  )
  const onPass = useCallback(
    (score: number): void => {
      setPassScore(score)
      persistPass(score)
    },
    [persistPass],
  )

  const onNavigate = useCallback(
    (href: string): void => {
      navigate(href)
    },
    [navigate],
  )

  const passed = view.status === 'pass'

  // #1446 Part B — after a persisted pass, route to the Part's `closing`
  // chapter (the flow's terminal exit); a role-less Part with no closing
  // falls back to the course landing.
  const closingChapter = data.chapters.find(c => c.role === 'closing')
  const continueHref = closingChapter
    ? `/courses/${courseId}/${partId}/${closingChapter.chapterId}`
    : `/courses/${courseId}`
  const continueLabel = closingChapter
    ? 'Continue to wrap-up'
    : 'Back to course'

  return (
    <div className={styles.page}>
      <div className={styles.container}>
        <div className={styles.header}>
          <span className={styles.eyebrow}>Part assessment</span>
          <h1 className={styles.title}>Show what you’ve mastered</h1>
          {data.assessmentPassed && !passed && (
            <span className={styles.passedNote} data-testid="already-passed">
              You’ve already cleared this Part — retake it any time.
            </span>
          )}
        </div>
        <AssessmentPhase
          control={control}
          onPass={onPass}
          remediation={remediation}
          onNavigate={onNavigate}
        />
        {passed && (
          <div className={styles.footer}>
            {recordPass.isError ? (
              <>
                <p
                  className={styles.passedNote}
                  role="alert"
                  data-testid="persist-error">
                  We couldn’t save your result yet — you’re not recorded as
                  passed until it saves. Check your connection and try again.
                </p>
                <button
                  type="button"
                  className={styles.backBtn}
                  data-testid="persist-retry"
                  onClick={() => {
                    if (passScore !== null) persistPass(passScore)
                  }}>
                  Try again
                </button>
              </>
            ) : recordPass.isSuccess ? (
              // Route to the closing (the Part flow's terminal exit) once
              // the pass has persisted; role-less Parts fall back to the
              // course landing.
              <button
                type="button"
                className={styles.backBtn}
                data-testid="assessment-back"
                onClick={() => navigate(continueHref)}>
                {continueLabel}
              </button>
            ) : (
              <button
                type="button"
                className={styles.backBtn}
                disabled
                data-testid="persist-pending">
                Saving your result…
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

/**
 * Resolve weak concepts to remediation links (umbrella #1357). An authored
 * `PartAssessment.remediation[concept]` override wins for the owning chapter +
 * objective ids + reference-card anchor; otherwise the chapter whose taught
 * `concept` matches the weak concept supplies them. The link `href` is the
 * chapter route plus an optional `#<referenceCard>` fragment (author-supplied
 * only). Concepts with no resolvable owning chapter are dropped.
 *
 * @param weakConcepts - the concepts the learner stumbled on this attempt
 * @param assessment - the Part assessment (its authored remediation overrides)
 * @param chapters - the Part's chapters (concept → owning chapter resolution)
 * @param loc - the course + part slugs for the link `href`
 * @returns the resolved remediation links, in weak-concept order
 */
function resolveRemediation(
  weakConcepts: NodeId[],
  assessment: PartAssessment,
  chapters: PartAssessmentChapter[],
  loc: { courseId: string; partId: string },
): RemediationLink[] {
  const links: RemediationLink[] = []
  for (const concept of weakConcepts) {
    const target = assessment.remediation?.[concept]
    const chapterId =
      target?.chapterId ??
      chapters.find(c => c.concept === concept)?.chapterId ??
      null
    if (!chapterId) continue

    const chapter = chapters.find(c => c.chapterId === chapterId)
    const chapterTitle = chapter?.title ?? chapterId
    const objectiveIds = target?.objectiveIds ?? chapter?.objectiveIds ?? []
    const card = target?.referenceCards?.[0]
    const href = `/courses/${loc.courseId}/${loc.partId}/${chapterId}${
      card ? `#${card}` : ''
    }`
    links.push({ concept, chapterTitle, href, objectiveIds })
  }
  return links
}
